/**
 * O visualizador de foto e vídeo dos documentos da oportunidade (25/09/2026).
 *
 * Abre sobre a tela, com o fundo escuro, e fecha com Esc, com clique fora da
 * mídia ou no ✕. PDF e os outros tipos não passam por aqui: abrem em aba nova,
 * onde o navegador tem o leitor dele.
 *
 * ── Empilhado sobre o card ───────────────────────────────────────────────
 *
 * O card da oportunidade já é um diálogo. O visualizador entra na mesma pilha
 * (`usePilhaDeDialogos`), e por isso o Esc fecha só ele, e não o card junto.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { usePilhaDeDialogos } from '@/shared/ui';

export interface MidiaParaVer {
  readonly tipo: 'imagem' | 'video';
  readonly url: string;
  readonly nome: string;
}

export function VisualizadorDeMidia({
  midia,
  aoFechar,
}: {
  midia: MidiaParaVer;
  aoFechar: () => void;
}): ReactNode {
  const noTopo = usePilhaDeDialogos();
  const fechar = useRef<HTMLButtonElement>(null);
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const devolverPara = document.activeElement as HTMLElement | null;
    fechar.current?.focus();
    const noTeclado = (evento: KeyboardEvent) => {
      if (!noTopo()) return;
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        aoFechar();
      }
      // O Tab não sai daqui: atrás do fundo escuro está o card, inerte.
      if (evento.key === 'Tab' && caixa.current) {
        const itens = Array.from(
          caixa.current.querySelectorAll<HTMLElement>('button, video[controls]'),
        );
        const primeiro = itens[0];
        const ultimo = itens[itens.length - 1];
        const atual = document.activeElement;
        if (!primeiro || !ultimo) return;
        if (evento.shiftKey && atual === primeiro) {
          evento.preventDefault();
          ultimo.focus();
        } else if (!evento.shiftKey && atual === ultimo) {
          evento.preventDefault();
          primeiro.focus();
        }
      }
    };
    document.addEventListener('keydown', noTeclado, true);
    return () => {
      document.removeEventListener('keydown', noTeclado, true);
      devolverPara?.focus?.();
    };
  }, [aoFechar, noTopo]);

  return (
    <div
      ref={caixa}
      className="visualizador"
      role="dialog"
      aria-modal="true"
      aria-label={midia.nome}
      onClick={(evento) => {
        if (evento.target === evento.currentTarget) aoFechar();
      }}
    >
      <button
        ref={fechar}
        type="button"
        className="visualizador__fechar"
        aria-label={`Fechar ${midia.nome}`}
        onClick={aoFechar}
      >
        ✕
      </button>
      {midia.tipo === 'imagem' ? (
        <img className="visualizador__midia" src={midia.url} alt={midia.nome} />
      ) : (
        <video className="visualizador__midia" src={midia.url} controls autoPlay>
          <track kind="captions" />
        </video>
      )}
    </div>
  );
}
